import { z } from 'zod';

import { zTransitionId } from '../common/identityTransition';
import { normalizeLeaseStatus, type LeaseStatus } from '../common/statuses';

/**
 * Zod schemas for leasing-related entities
 * These provide runtime validation and type inference
 */

// Listing object (compose refinements for full document vs POST create)
export const listingObjectSchema = z
  .object({
    id: z.string(),
    propertyId: z.string(),
    property: z.string().nullish(),
    unitId: z.string().nullish(),
    floorPlanId: z.string().nullish(),
    headline: z.string().min(1, 'Headline is required'),
    description: z.string(),
    monthlyRent: z.number().nonnegative(),
    deposit: z.number().nonnegative(),
    availableDate: z.string(),
    status: z.enum(['Active', 'Draft', 'Pending', 'Leased'] as const),
    images: z.array(z.string()),
    amenities: z.array(z.string()),
    views: z.number().nonnegative().nullish(),
    inquiries: z.number().nonnegative().nullish(),
    featured: z.boolean().nullish(),
    petsAllowed: z.boolean().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    deletedAt: z.string().datetime().nullable().optional(),
  })
  .passthrough();

export const listingSchema = listingObjectSchema.superRefine((data, ctx) => {
  const u = typeof data.unitId === 'string' ? data.unitId.trim() : '';
  const f = typeof data.floorPlanId === 'string' ? data.floorPlanId.trim() : '';
  if (u && f) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Cannot set both unitId and floorPlanId on a listing',
      path: ['unitId'],
    });
  }
});

/** POST /leasing/listings — no id; requires exactly one of unitId or floorPlanId (after trim). */
export const listingCreateBodySchema = listingObjectSchema
  .omit({ id: true })
  .superRefine((data, ctx) => {
    const u = typeof data.unitId === 'string' ? data.unitId.trim() : '';
    const f = typeof data.floorPlanId === 'string' ? data.floorPlanId.trim() : '';
    if (!u && !f) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Provide unitId or floorPlanId for the listing.',
        path: ['unitId'],
      });
    }
    if (u && f) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Cannot set both unitId and floorPlanId on a listing',
        path: ['unitId'],
      });
    }
  });

export type Listing = z.infer<typeof listingSchema>;

// TourSlot schema (from existing schemas.ts)
export const tourSlotSchema = z.object({
  id: z.string(),
  propertyId: z.string(),
  date: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  maxAttendees: z.number().positive(),
  currentAttendees: z.number().nonnegative(),
  host: z.string(),
  type: z.enum(['Open House', 'Private Block', 'Virtual', 'Private'] as const),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

export type TourSlot = z.infer<typeof tourSlotSchema>;

// Lead schema (object only — compose refinements for full document vs PATCH)
export const leadObjectSchema = z
  .object({
    id: z.string(),
    name: z.string().min(1, 'Lead name is required'),
    email: z.string().email().or(z.literal('')),
    phone: z.string(),
    operatingOrganizationId: z.preprocess(
      (val) => (val === '' ? undefined : val),
      z.string().optional(),
    ),
    propertyId: z.string().nullish(),
    unitId: z.string().nullish(),
    floorPlanId: z.string().nullish(),
    propertyName: z.string().nullish(),
    status: z.enum(['New Inquiry', 'Contacted', 'Tour Scheduled', 'Tour Completed', 'Application Sent', 'Leased', 'Lost'] as const),
    source: z.string(),
    lastAction: z.string(),
    notes: z.string().optional(),
    tourSlotId: z.string().nullable().optional(),
    questionnaireResponse: z.record(z.string()).nullable().optional(),
    reminderSentAt: z.string().nullable().optional(),
    selfScheduledAt: z.string().nullable().optional(),
    createdAt: z.string(),
    piiDeletedAt: z.string().nullable().optional(),
    deletedAt: z.string().optional(),
    deletedBy: z.string().optional(),
  })
  .passthrough();

function refineLeadXor(
  data: { unitId?: string | null; floorPlanId?: string | null },
  ctx: z.RefinementCtx,
): void {
  if (data.unitId && data.floorPlanId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Cannot set both unitId and floorPlanId on a lead',
      path: ['unitId'],
    });
  }
}

function refineLeadOrgOrProperty(
  data: { operatingOrganizationId?: string; propertyId?: string | null },
  ctx: z.RefinementCtx,
): void {
  if (!data.operatingOrganizationId && !data.propertyId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Provide propertyId (org is derived server-side) or operatingOrganizationId for corporate leads',
      path: ['propertyId'],
    });
  }
}

export const leadSchema = leadObjectSchema.superRefine((data, ctx) => {
  refineLeadXor(data, ctx);
  refineLeadOrgOrProperty(data, ctx);
});

/** PATCH bodies: XOR only (org/property resolved on server when omitted). */
export const leadPatchSchema = leadObjectSchema.partial().superRefine((data, ctx) => {
  refineLeadXor(data, ctx);
});

export type LeadParsed = z.infer<typeof leadSchema>;

// Application schema — income/creditScore/unit nullable for API/Prisma (coerce for validation)
export const applicationObjectSchema = z
  .object({
  id: z.string(),
  applicantName: z.string().min(1, 'Applicant name is required'),
  propertyId: z.string().min(1),
  unitId: z.string().nullish(),
  floorPlanId: z.string().nullish(),
  propertyName: z.string().nullish(),
  income: z.number().nonnegative('Income must be non-negative').nullish().transform(v => v ?? 0),
  creditScore: z.number().min(300).max(850, 'Credit score must be between 300 and 850').nullish().transform(v => (v != null && v >= 300 && v <= 850 ? v : 300)),
  score: z.number().nullish(),
  status: z.string().transform(s => {
    const allowed = ['Screening', 'Approved', 'Approved - Pending Lease', 'Conditional', 'Denied', 'Pending', 'Under Review', 'Pending Docs', 'Rejected'] as const;
    return allowed.includes(s as (typeof allowed)[number]) ? (s as (typeof allowed)[number]) : 'Pending';
  }),
  date: z.string(),
  moveInDate: z.string().nullish(),

  // Personal Information
  firstName: z.string().nullish(),
  middleName: z.string().nullish(),
  lastName: z.string().nullish(),
  email: z.string().email().nullish().or(z.literal('')),
  phone: z.string().nullish(),
  dateOfBirth: z.string().nullish(),
  ssnLast4: z.string().nullish(),
  yearsAtAddress: z.number().nonnegative().nullish(),
  monthsAtAddress: z.number().nonnegative().nullish(),

  // Employment
  employmentStatus: z.string().nullish(),
  employerName: z.string().nullish(),
  jobTitle: z.string().nullish(),
  employerPhone: z.string().nullish(),
  employerAddress: z.string().nullish(),
  supervisorName: z.string().nullish(),
  supervisorPhone: z.string().nullish(),
  employmentDuration: z.string().nullish(),
  yearsEmployed: z.number().nonnegative().nullish(),
  monthsEmployed: z.number().nonnegative().nullish(),
  monthlyIncome: z.number().nonnegative().nullish(),
  additionalIncome: z.number().nonnegative().nullish(),
  additionalIncomeSource: z.string().nullish(),

  // Rental History
  currentAddress: z.string().nullish(),
  currentLandlordName: z.string().nullish(),
  currentLandlordPhone: z.string().nullish(),
  previousAddress: z.string().nullish(),
  previousLandlordName: z.string().nullish(),
  previousLandlordPhone: z.string().nullish(),
  rentalHistoryYears: z.number().nonnegative().nullish(),

  // References
  reference1Name: z.string().nullish(),
  reference1Phone: z.string().nullish(),
  reference1Relationship: z.string().nullish(),
  reference2Name: z.string().nullish(),
  reference2Phone: z.string().nullish(),
  reference2Relationship: z.string().nullish(),

  // Legal/Background
  hasEvictions: z.boolean().nullish(),
  hasCriminalHistory: z.boolean().nullish(),
  hasBankruptcy: z.boolean().nullish(),
  legalNotes: z.string().nullish(),

  // Pet Information
  hasPets: z.boolean().nullish(),
  petCount: z.number().nonnegative().nullish(),
  petTypes: z.array(z.string()).nullish(),

  // Co-signer/Guarantor
  hasCoSigner: z.boolean().nullish(),
  coSignerName: z.string().nullish(),
  coSignerEmail: z.string().email().nullish().or(z.literal('')),
  coSignerPhone: z.string().nullish(),
  coSignerIncome: z.number().nonnegative().nullish(),

  // Source & Tracking
  leadId: z.string().nullish(),
  source: z.string().nullish(),
  submittedAt: z.string().nullish(),
  screeningNotes: z.string().nullish(),

  // Background Check Integration (API may return lowercase or snake_case)
  backgroundCheckRequestId: z.string().nullish(),
  backgroundCheckStatus: z
    .enum(['Pending', 'In Progress', 'Completed', 'Failed'] as const)
    .or(z.string().transform(s => {
      const lower = String(s).toLowerCase().replaceAll(/\s+/g, ' ');
      if (lower === 'pending' || lower === 'in_progress') return 'Pending';
      if (lower === 'in progress') return 'In Progress';
      if (lower === 'completed') return 'Completed';
      if (lower === 'failed') return 'Failed';
      return 'Pending';
    }))
    .nullish(),
  backgroundCheckCompletedAt: z.string().nullish(),

  // Authorization / Consents (Figma-inspired)
  backgroundCheckConsent: z.boolean().nullish(),
  creditCheckConsent: z.boolean().nullish(),
  rentalHistoryConsent: z.boolean().nullish(),
  termsAccepted: z.boolean().nullish(),
  signature: z.string().nullish(),
  signatureDate: z.string().nullish(),

  // Optional document uploads
  applicationDocuments: z
    .array(z.object({ type: z.enum(['id', 'income']), url: z.string() }))
    .nullish(),
}).passthrough();

function refineApplicationXor(
  data: { unitId?: string | null; floorPlanId?: string | null },
  ctx: z.RefinementCtx,
): void {
  if (data.unitId && data.floorPlanId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Cannot set both unitId and floorPlanId on an application',
      path: ['unitId'],
    });
  }
}

export const applicationSchema = applicationObjectSchema.superRefine(refineApplicationXor);

// Infer TypeScript type from schema
export type ApplicationParsed = z.infer<typeof applicationSchema>;

// Partial schema for updates (id optional at Zod layer; routes may require it separately)
export const applicationUpdateSchema = applicationObjectSchema.partial().superRefine(refineApplicationXor);
export type ApplicationUpdate = z.infer<typeof applicationUpdateSchema>;

// Lease schema
const leaseSignatureSchema = z.object({
  signed: z.boolean(),
  signedDate: z.string().nullish(),
  signatureData: z.string().nullish(),
  signedBy: z.string().nullish(),
});

const leaseWitnessSchema = z.object({
  name: z.string(),
  signed: z.boolean(),
  signedDate: z.string().nullish(),
  signatureData: z.string().nullish(),
});

const leaseSignaturesSchema = z.object({
  tenant: leaseSignatureSchema.optional(),
  landlord: leaseSignatureSchema.optional(),
  witnesses: z.array(leaseWitnessSchema).optional(),
});

/** Integer cents on JSON wire (DB may use BigInt; serializers may add *CentsString). */
const zIntCents = z.coerce.number().int().nonnegative();

const leaseAddonSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    amountCents: zIntCents,
    amountCentsString: z.string().optional(),
    /** Dollars derived from amountCents (API responses). */
    amount: z.number().nonnegative(),
    recurring: z.boolean(),
  })
  .strict();

const leaseRenewalHistorySchema = z.object({
  previousLeaseId: z.string(),
  renewalDate: z.string(),
  newEndDate: z.string(),
  changes: z.string().nullish(),
});

const leaseOccupantSchema = z.object({
  name: z.string(),
  relation: z.string(),
  age: z.string(),
});

const leaseVehicleSchema = z.object({
  make: z.string(),
  model: z.string(),
  plate: z.string(),
  permit: z.string(),
});

const leasePetSchema = z.object({
  name: z.string(),
  type: z.string(),
  breed: z.string().nullish(),
  weight: z.number().nullish(),
});

// Lease Addendum schema
export const leaseAddendumSchema = z.object({
  id: zTransitionId,
  leaseId: zTransitionId,
  type: z.enum(['rent_change', 'term_extension', 'pet_add', 'occupant_add', 'other'] as const),
  effectiveDate: z.string(),
  description: z.string().min(1, 'Description is required'),
  /**
   * Addendum-specific changes structure.
   * 
   * Structure varies by addendum type:
   * - rent_change: { type: 'rent_change', newRent: number, previousRent: number, ... }
   * - term_extension: { type: 'term_extension', newEndDate: string, previousEndDate: string, ... }
   * - pet_add: { type: 'pet_add', petType: string, petName: string, ... }
   * - occupant_add: { type: 'occupant_add', occupantName: string, occupantRelation: string, ... }
   * - other: { type: 'other', description: string, changes: Record<string, unknown> }
   * 
   * @see addendumTypes.ts for type-safe discriminated union types (AddendumChanges)
   */
  changes: z.record(z.unknown()), // Flexible changes object - use AddendumChanges union type for type-safe access
  signedBy: z.string().nullish(),
  signedAt: z.string().nullish(),
  createdAt: z.string(),
});

export type LeaseAddendum = z.infer<typeof leaseAddendumSchema>;

// Coerce date from API/Prisma (Date or string)
const leaseDateLike = z.union([z.string(), z.date()]).transform(v => (v instanceof Date ? v.toISOString().split('T')[0] : String(v)));
const leaseStatusCoerce = z
  .string()
  .transform((s) => normalizeLeaseStatus(s)) as z.ZodType<LeaseStatus>;

export const leaseSchema = z.object({
  id: zTransitionId,
  referenceNumber: z.string().nullish(),
  tenantId: z.string(),
  unitId: zTransitionId,
  propertyId: zTransitionId,

  // Basic lease info
  startDate: leaseDateLike,
  endDate: leaseDateLike,
  rentAmountCents: zIntCents,
  rentAmountCentsString: z.string().optional(),
  securityDepositCents: zIntCents,
  securityDepositCentsString: z.string().optional(),
  petDepositCents: zIntCents.nullish(),
  petDepositCentsString: z.string().nullish(),
  parkingFeeCents: zIntCents.nullish(),
  parkingFeeCentsString: z.string().nullish(),
  /** Legacy/display dollars (dual-read from cents in API). */
  monthlyRent: z.number().nonnegative(),
  rentDueDay: z.number().int().min(1).max(31).optional().default(1),
  deposit: z.number().nonnegative().nullish().default(0),
  petDeposit: z.number().nonnegative().nullish(),
  parkingFee: z.number().nonnegative().nullish(),

  // Status
  status: leaseStatusCoerce,

  // Terms
  leaseTerm: z.number().positive().nullish().default(12),
  autoRenew: z.boolean().nullish(),
  renewalTerm: z.number().positive().nullish(),

  // Signatures
  signatures: leaseSignaturesSchema.nullish(),

  // Addons
  addons: z.array(leaseAddonSchema).nullish(),

  // Addendums
  addendums: z.array(leaseAddendumSchema).nullish(),

  // Renewal history
  renewalHistory: z.array(leaseRenewalHistorySchema).nullish(),

  // Termination
  terminationDate: z.string().nullish(),
  terminationReason: z.string().nullish(),
  terminationNotes: z.string().nullish(),

  // Additional info
  occupants: z.array(leaseOccupantSchema).nullish(),
  vehicles: z.array(leaseVehicleSchema).nullish(),
  pets: z.array(leasePetSchema).nullish(),

  // E-signature (API may return different casing)
  esignatureStatus: z.string().nullish(),
  eSignatureStatus: z.string().optional().nullable().transform(v => {
    if (v == null || v === '') return undefined;
    const allowed = ['Not Started', 'Pending', 'Completed', 'Failed'] as const;
    return allowed.includes(v as (typeof allowed)[number]) ? (v as (typeof allowed)[number]) : undefined;
  }),
  eSignatureEnvelopeId: z.string().nullish(),
  /** Prisma/storage field name (same value as `eSignatureEnvelopeId`; squeezed responses may omit). */
  esignatureEnvelopeId: z.string().nullish(),
  eSignatureCompletedAt: z.string().nullish(),
  documentDraftKey: z.string().nullish(),
  documentSignedKey: z.string().nullish(),

  // Dates
  createdAt: z.union([z.string(), z.date()]).nullish(),
  updatedAt: z.union([z.string(), z.date()]).nullish(),
  signedAt: z.string().nullish(),

  /** Join labels (repository-shaped API responses). */
  tenantName: z.string().optional(),
  propertyName: z.string().optional(),
  unitNumber: z.string().optional(),
  applicationId: z.string().nullish(),
  lateFeeConfig: z
    .object({
      enabled: z.boolean(),
      type: z.enum(['fixed', 'percentage'] as const),
      amount: z.number().optional(),
      gracePeriodDays: z.number().optional(),
      maxLateFee: z.number().optional(),
    })
    .optional(),
  legacyMetadata: z.record(z.unknown()).nullable().optional(),
}).strict();

// Infer TypeScript type from schema
export type Lease = z.infer<typeof leaseSchema>;

// Partial schema for updates
export const leaseUpdateSchema = leaseSchema.partial().required({ id: true });
export type LeaseUpdate = z.infer<typeof leaseUpdateSchema>;

const zRenewalCents = z.coerce.number().int().nonnegative();

// RenewalOffer schema (API / validation; cents canonical, `monthlyRent` dollars for display)
export const renewalOfferSchema = z
  .object({
    id: zTransitionId,
    leaseId: zTransitionId,
    monthlyRentCents: zRenewalCents,
    securityDepositCents: zRenewalCents.nullish(),
    monthlyRent: z.number().nonnegative(),
    leaseTerm: z.number().int().positive(),
    endDate: z.string(),
    status: z
      .enum([
        'pending',
        'accepted',
        'rejected',
        'expired',
        'Sent',
        'Draft',
        'Accepted',
        'Rejected',
        'Expired',
      ] as const)
      .optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
    expiresAt: z.string().optional(),
    sentAt: z.string().optional(),
  })
  .strict();

export type RenewalOffer = z.infer<typeof renewalOfferSchema>;

/** `GET /leasing/security-deposits` list row (summary fields only). */
export const securityDepositListEntrySchema = z
  .object({
    id: z.string(),
    tenantId: z.string(),
    amount: z.number(),
    status: z.string(),
    dateCollected: z.string(),
    returnDeadlineDate: z.string().nullable().optional(),
  })
  .strict();

export type SecurityDepositListEntry = z.infer<typeof securityDepositListEntrySchema>;

/** `GET /leasing/docusign/configured` */
export const docusignConfiguredResponseSchema = z
  .object({
    configured: z.boolean(),
  })
  .strict();

export type DocusignConfiguredResponse = z.infer<typeof docusignConfiguredResponseSchema>;

// BackgroundCheckRequest schema (simplified for query response)
export const backgroundCheckSchema = z.object({
  id: z.string(),
  applicationId: z.string(),
  provider: z.enum(['checkr', 'transunion'] as const),
  packageType: z.string(),
  status: z.enum(['pending', 'in_progress', 'completed', 'failed', 'cancelled'] as const),
  reportId: z.string().optional(),
  requestedAt: z.string(),
  completedAt: z.string().optional(),
  overallStatus: z.enum(['pass', 'fail', 'conditional', 'pending'] as const).optional(),
  riskLevel: z.enum(['low', 'medium', 'high'] as const).optional(),
  error: z.string().optional(),
}).passthrough(); // Allow additional fields from BackgroundCheckResults

export type BackgroundCheck = z.infer<typeof backgroundCheckSchema>;

export { tenantSchema } from './tenantSchemas';
